import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useSaveKey, withSaveKey } from "@/lib/saveOnce";
import { money, fmtDate } from "@/lib/format";
import { isOverpaid, overpaidBy } from "@/lib/advanceOwed";
import { curCode, sameCurrency } from "@/lib/currencies";
import { noAutomaticRateFor, rateRefusal, BAD_TYPED_RATE, typedRateValue } from "@/lib/fxPanel";
import { MoneyInput } from "@/lib/accountingFormat";
import { toast } from "sonner";
import { Btn } from "@/components/common";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { NotesField } from "@/components/NotesField";
import { ViewButton, RecordView } from "@/components/RecordView";
import { repaymentFields, RECORD_TITLES } from "@/lib/recordFields";
import { useParties } from "@/lib/parties";
import { usePaymentSources, psLabel, psOwnerName, psScopedForStudent } from "@/lib/payments";
import { accountsPhrase } from "@/lib/accountsPhrase";
import { PaidAmount } from "@/components/PaidAmount";

const CAP = "text-xs font-bold uppercase tracking-wide text-[#6b7280]";
const ADD = "__add__";
const MONEY = /^\d+(\.\d{1,2})?$/;   // a money amount as the box holds it
const PICK = "h-9 w-48 max-w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm text-[#0A0A0A]";

// The accounts a repayment moves between — the one rule for both the advance
// form's reimbursement and Record repayment. From = the student's own bank
// accounts; To = the payer's accounts in reach of that student.
export function useRepaymentAccounts(studentId, chosenFromId, chosenToId) {
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: parties } = useParties();
  // Every account, archived and removed included, so an account a record
  // already names stays selectable; psScopedForStudent hides the rest.
  const { data: psData } = usePaymentSources(true);
  const all = psData?.sources || [];
  const label = (x) => psLabel(x, psOwnerName(x, students, parties));
  const fromAccounts = psScopedForStudent(all.filter((b) => b.owner_type === "student" && b.student_id === studentId), studentId, chosenFromId)
    .map((b) => ({ id: b.id, label: label(b) }));
  const toAccounts = psScopedForStudent(all, studentId, chosenToId)
    .map((s) => ({ id: s.id, label: label(s) }));
  return { fromAccounts, toAccounts };
}

// One account picker: "— None —", the accounts, then "＋ Add account…".
export function AccountPick({ value, onChange, options, onAdd, testid, className }) {
  return (
    <select value={value || ""} data-testid={testid} className={className}
      onChange={(e) => (e.target.value === ADD ? onAdd() : onChange(e.target.value || null))}>
      <option value="">— None —</option>
      {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      <option value={ADD}>＋ Add account…</option>
    </select>
  );
}

// Every account's label by id, archived and removed ones included, so an old
// repayment still names the account it moved between.
function useAccountLabels() {
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: parties } = useParties();
  const { data: psData } = usePaymentSources(true);
  const byId = {};
  for (const s of psData?.sources || []) byId[s.id] = psLabel(s, psOwnerName(s, students, parties));
  return byId;
}

// One line of an advance's repayment history, the same on both screens:
// date, amount and the two accounts. The note is in the details view.
export function RepaymentLine({ r, a }) {
  const labels = useAccountLabels();
  const [open, setOpen] = useState(false);
  const from = r.from_account ? labels[r.from_account] : null;
  const to = r.to_account ? labels[r.to_account] : null;
  const accounts = accountsPhrase(from, to);
  return (
    <span className="inline-flex items-center gap-1">
      <span data-testid={`advance-repay-line-${r.id}`}>{fmtDate(r.date)} · <PaidAmount testid={`advance-repay-amount-${r.id}`}>{money(r.amount, curCode(r.currency || a.repay_currency || a.currency, "USD"))}</PaidAmount>{accounts ? ` · ${accounts}` : ""}</span>
      <ViewButton size={13} onClick={() => setOpen(true)} testid={`advance-repay-view-${r.id}`} />
      <RecordView open={open} onClose={() => setOpen(false)} title={RECORD_TITLES.repayment}
        fields={repaymentFields(r, a, (id) => labels[id] || null)} testid={`advance-repay-detail-${r.id}`} />
    </span>
  );
}

// The one "Record repayment" form, used by Advances and the dashboard panel.
// Mount it when opened: each opening gets its own fields and save key.
export function RecordRepaymentForm({ a, onDone }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { fromAccounts, toAccounts } = useRepaymentAccounts(a.student_id);
  // Pre-filled with what is owed only when it is owed in the currency being paid;
  // across currencies the box starts empty (owner, 17 Oct 2026).
  const cur = curCode(a.repay_currency || a.currency, "USD");
  const advCur = curCode(a.currency, "USD");
  const [form, setForm] = useState({ amount: sameCurrency(cur, advCur) ? a.remaining.toFixed(2) : "", date: "", from_account: null, to_account: null, note: "" });
  const [busy, setBusy] = useState(false);
  // One key for this opening, sent with every press of Confirm. (ZEN-759)
  const saveKey = useSaveKey(`opening:${a.id}`);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  // ZEN-770: the line is always the server's own figure, for exactly the
  // inputs on screen; a typed rate only after the server asks (round 5).
  const [preview, setPreview] = useState(null);   // { key, data } of the last answer
  const [rateNeeded, setRateNeeded] = useState(false);
  const [typedRate, setTypedRate] = useState("");
  // A1 shows only once something is typed or Confirm is pressed (owner, 17 Oct 2026).
  const [rateTouched, setRateTouched] = useState(false);
  const asked = useRef(0);
  // The currencies this form shows, so the server refuses if they changed since (K7).
  const pairShown = { shown_currency: advCur, shown_repay_currency: cur };
  // No date yet: no preview and no "remaining after this" (round 3).
  const rateDate = form.date && form.date.length >= 10 ? form.date : null;
  // A typed rate belongs to its own date and currency pair: any change clears it.
  useEffect(() => { setRateNeeded(false); setTypedRate(""); setRateTouched(false); }, [form.date, cur, advCur]);
  const rate = rateNeeded ? typedRateValue(typedRate) : undefined;
  const badRate = rateNeeded && rate === null;
  const amount = MONEY.test(form.amount || "") && Number(form.amount) > 0 ? Number(form.amount) : null;
  const body = rateDate && amount !== null && !badRate
    ? { amount, date: rateDate, ...pairShown, ...(rate !== undefined ? { fx_rate: rate } : {}) } : null;
  const key = body ? JSON.stringify(body) : null;
  useEffect(() => {
    const n = ++asked.current;
    if (!key) return undefined;
    const t = setTimeout(() => {
      const done = (data) => { if (n === asked.current) setPreview({ key, data }); };
      api.post(`/advances/${a.id}/repay-preview`, JSON.parse(key))
        .then((res) => done(res.data || null)).catch(() => done(null));
    }, 300);
    return () => clearTimeout(t);
  }, [a.id, key]);
  const shown = key && preview?.key === key ? preview.data : null;
  const after = shown?.remaining_after ?? null;
  // "(estimate)" when any repayment in the figure is estimated, not only this one (round 6).
  const estimate = shown?.remaining_is_estimate === true;

  const save = async () => {
    if (badRate) { setRateTouched(true); return; }   // the rate box says why
    setBusy(true);
    try {
      const body = { amount: parseFloat(form.amount) || 0, date: form.date, from_account: form.from_account, to_account: form.to_account, note: form.note, ...pairShown };
      if (rate) body.fx_rate = rate;
      await api.post(`/advances/${a.id}/repay`, body, withSaveKey(saveKey));
      toast.success(`Repayment recorded — ${money(parseFloat(form.amount), cur)}`);
      qc.invalidateQueries({ queryKey: ["advances"] });
      onDone?.();
    } catch (e) {
      // Refused for want of a rate: nothing was stored, so the same key is safe to resend.
      if (e?.response?.status === 422 && e.response.data?.detail?.rate_required) { setRateNeeded(true); setRateTouched(false); }
      else toast.error(rateRefusal(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-[#C2C9D1] bg-[#F8FAFC] p-2.5" data-testid={`advance-repay-form-${a.id}`}>
      <label className="flex flex-col"><span className={`mb-1 ${CAP}`}>Amount</span>
        <MoneyInput currency={cur} className="!h-9 !w-36" value={form.amount} onChange={set("amount")} placeholder="" testid={`advance-repay-amount-${a.id}`} /></label>
      <label className="flex flex-col"><span className={`mb-1 ${CAP}`}>Date</span>
        <UnifiedDateInput value={form.date} onChange={set("date")} testid={`advance-repay-date-${a.id}`} preset="softFutureDate" /></label>
      <label className="flex flex-col"><span className={`mb-1 ${CAP}`}>From account</span>
        <AccountPick value={form.from_account} onChange={set("from_account")} options={fromAccounts} onAdd={() => nav("/accounts")} testid={`advance-repay-from-acct-${a.id}`} className={PICK} /></label>
      <label className="flex flex-col"><span className={`mb-1 ${CAP}`}>To account</span>
        <AccountPick value={form.to_account} onChange={set("to_account")} options={toAccounts} onAdd={() => nav("/accounts")} testid={`advance-repay-to-acct-${a.id}`} className={PICK} /></label>
      {rateNeeded && (
        <div className="w-full" data-testid={`advance-repay-rate-needed-${a.id}`}>
          <p className="mb-1 text-xs text-[#92600A]">{noAutomaticRateFor(cur)}</p>
          <label className="flex flex-col"><span className={`mb-1 ${CAP}`}>Exchange rate (1 {cur} = ? {advCur})</span>
            <input type="text" inputMode="decimal" value={typedRate} onChange={(e) => { setTypedRate(e.target.value); setRateTouched(true); }}
              data-testid={`advance-repay-fx-rate-${a.id}`} className="h-9 w-40 rounded-md border border-[#C2C9D1] px-2 text-sm text-[#0A0A0A]" /></label>
          {badRate && rateTouched && <p className="mt-1 text-xs text-[#B91C1C]" data-testid={`advance-repay-fx-rate-bad-${a.id}`}>{BAD_TYPED_RATE}</p>}
        </div>
      )}
      <NotesField className="w-full" value={form.note} onChange={set("note")} testid={`advance-repay-note-${a.id}`} />
      <Btn variant="green" className="h-9" disabled={busy} onClick={save} data-testid={`advance-repay-confirm-${a.id}`}>{busy ? "Saving..." : "Confirm"}</Btn>
      <Btn variant="ghost" className="h-9" onClick={() => onDone?.()}>Cancel</Btn>
      <div className="w-full text-[11px] text-[#6b7280]" data-testid={`advance-repay-preview-${a.id}`}>
        Outstanding {money(a.remaining, a.currency)}{after !== null && <> · <b className="text-[#D97706]">{isOverpaid(after) ? overpaidBy(after, a.currency) : `${money(Math.abs(after) <= 0.005 ? 0 : after, a.currency)} remaining after this`}{estimate ? " (estimate)" : ""}</b></>}
      </div>
    </div>
  );
}

export default RecordRepaymentForm;
